import React, {useMemo} from 'react';
import './FactionUnitSkinsCarousel.css';
import FactionsCarousel from '../EmblaCarousel/FactionsCarousel';
import {FACTION_CONFIGS} from '../../configs/factions';
import {FACTION_UNIT_SKIN_CONFIGS} from '../../configs/factionUnitsSkins';
import {UNIT_TYPES} from '../../configs/units';
import {UNIT_D4_MAX_SHOOTING_RANGE} from '../../constants/units';
import {getFactionUnitSkinIconPath} from '../../utils/factions/getFactionUnitSkinIconPath';

const OPTIONS = {loop: true};

const UNIT_SKIN_DISPLAY_TYPES = [
  {unitType: UNIT_TYPES.D6, label: 'Пехота'},
  {unitType: UNIT_TYPES.D10, label: 'Тяжелая пехота'},
  {unitType: UNIT_TYPES.D20, label: 'Элитная пехота'},
  {unitType: UNIT_TYPES.D8, label: 'Лёгкая кавалерия'},
  {unitType: UNIT_TYPES.D12, label: 'Тяжёлая кавалерия'},
  {unitType: UNIT_TYPES.D4, label: 'Катапульта'},
];

/**
 * Элементы карусели образов отрядов фракции: применимые фракции типы отрядов
 * с фракционной спецификацией характеристик (unitTypesSpec замещает поля
 * стандартного конфига точечно), сдвинутые по кругу так, чтобы первым шел
 * d20 (элитная пехота) — взаимный порядок остальных типов сохраняется.
 */
export const buildFactionUnitSkinItems = (factionId, isOwned) => {
  const faction = FACTION_CONFIGS.find((config) => config.id === factionId);
  const unitSkinConfig = FACTION_UNIT_SKIN_CONFIGS.find((config) => config.factionId === factionId);
  if (!faction || !unitSkinConfig) return [];

  const applicableTypes = faction.unitTypes
    ? UNIT_SKIN_DISPLAY_TYPES.filter((entry) => faction.unitTypes.includes(entry.unitType.id))
    : UNIT_SKIN_DISPLAY_TYPES;
  const items = applicableTypes.map((entry) => {
    const spec = faction.unitTypesSpec?.[entry.unitType.id];
    return {
      id: `${unitSkinConfig.id}_${entry.unitType.id}`,
      unitTypeId: entry.unitType.id,
      factionId,
      name: spec?.name ?? entry.label,
      imagePath: getFactionUnitSkinIconPath(entry.unitType.id, factionId),
      isOwned,
      priceNumens: unitSkinConfig.priceNumens,
      unitPrice: spec?.price ?? entry.unitType.price,
      unitMaxSize: spec?.maxSize ?? entry.unitType.maxSize,
      unitSpeed: spec?.speed ?? entry.unitType.speed,
      isFlying: Boolean(spec?.isFlying),
      // Дальность стрельбы: из спецификации фракции (например, все отряды
      // эльфов), а по умолчанию — только у катапульты (D4)
      unitShootingRange: spec?.shootingRange
        ?? (entry.unitType.id === UNIT_TYPES.D4.id ? UNIT_D4_MAX_SHOOTING_RANGE : null),
    };
  });

  const d20Index = items.findIndex((item) => item.unitTypeId === UNIT_TYPES.D20.id);
  if (d20Index <= 0) {
    return items;
  }
  return [
    ...items.slice(d20Index),
    ...items.slice(0, d20Index),
  ];
};

/**
 * Карусель образов отрядов фракции (FactionPage «Образы отрядов» и модалка
 * награды FACTION_UNIT_SKIN): masked-иконки отрядов, залитые цветом выбранного
 * скина фракции, с характеристиками отряда в описании. Вся логика покупки —
 * снаружи (onRitualStart/selectButtonLabel/getItemOverlay).
 */
const FactionUnitSkinsCarousel = ({
                                    factionId,
                                    iconColor,
                                    isOwned,
                                    ritualPhase,
                                    onRitualStart,
                                    onSelect,
                                    onSelectedIndexChange,
                                    getItemOverlay,
                                    selectButtonLabel,
                                    isSelectDisabled,
                                    showDescription = true,
                                    pageId,
                                    selectButtonId = 'unit_skin_action',
                                  }) => {
  const items = useMemo(
    () => buildFactionUnitSkinItems(factionId, isOwned),
    [factionId, isOwned]
  );

  const slides = useMemo(
    () => Array.from(Array(items.length).keys()),
    [items.length]
  );

  if (items.length === 0) return null;

  return (
    <FactionsCarousel
      slides={slides}
      items={items}
      options={OPTIONS}
      onRitualStart={onRitualStart}
      onSelect={onSelect}
      onSelectedIndexChange={onSelectedIndexChange}
      ritualPhase={ritualPhase}
      getItemImagePath={(item) => item.imagePath}
      getItemImageElement={(item) => (
        <div
          className="faction-unit-skins-carousel-icon"
          style={{
            backgroundColor: iconColor,
            WebkitMaskImage: `url(${item.imagePath})`,
            maskImage: `url(${item.imagePath})`,
          }}
          role="img"
          aria-label={item.name}
        />
      )}
      getItemName={(item) => item.name}
      getItemDescription={(item) => (
        <div className="faction-unit-skins-carousel-stats">
          <span
            className="faction-unit-skins-carousel-stat text-coins"
            title={'Цена найма отряда'}
          >
            <img className="faction-unit-skins-carousel-stat-icon" src="/img/icons/coins.png" alt="Цена найма"/>
            {item.unitPrice}
          </span>
          <span
            className="faction-unit-skins-carousel-stat faction-unit-skins-carousel-stat_unit-size text-secondary"
            title={'Максимальный размер отряда'}
          >
            <img
              className="faction-unit-skins-carousel-stat-icon"
              src="/img/icons/unit_size_grey.png"
              alt="Максимальный размер"
            />
            {item.unitMaxSize}
          </span>
          {item.isFlying ? (
            <span
              className="faction-unit-skins-carousel-stat faction-unit-skins-carousel-stat_flying"
              title={'Летающий отряд: дальность перемещения в фазе Войны'}
            >
              <img
                className="faction-unit-skins-carousel-stat-icon"
                src="/img/icons/fly.png"
                alt="Дальность полёта"
              />
              {item.unitSpeed}
            </span>
          ) : (
            <span
              className="faction-unit-skins-carousel-stat text-controls"
              title={'Дальность перемещения отряда в фазе Войны'}
            >
              <img
                className="faction-unit-skins-carousel-stat-icon"
                src="/img/icons/speed.png"
                alt="Дальность перемещения"
              />
              {item.unitSpeed}
            </span>
          )}
          {item.unitShootingRange && (
            <span
              className="faction-unit-skins-carousel-stat text-war"
              title={'Дальность стрельбы отряда'}
            >
              <img
                className="faction-unit-skins-carousel-stat-icon"
                src="/img/icons/shooting.png"
                alt="Дальность стрельбы"
              />
              {item.unitShootingRange}
            </span>
          )}
        </div>
      )}
      getItemOverlay={getItemOverlay}
      getItemTextColor={() => iconColor}
      isItemAvailable={() => true}
      isSelectDisabled={isSelectDisabled || ((item) => item.isOwned)}
      selectButtonLabel={selectButtonLabel || 'Открыто'}
      showDescription={showDescription}
      showDetailsButton={false}
      pageId={pageId}
      selectButtonId={selectButtonId}
      selectButtonNeutralColor={true}
    />
  );
};

export default FactionUnitSkinsCarousel;
